import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'

// ─── Types ────────────────────────────────────────────────────────────────────
interface ElapCheck {
  id: string; label: string; status: 'pass' | 'fail' | 'warn' | 'missing'; detail: string; required: boolean
}
interface LossEvent {
  _id: string; yardId: string; discoveredDate: string; filingDeadline: string
  coloniesLost: number; hivesDestroyed: number; cause: string; fsaNoticeFiled: boolean; notes?: string
}
interface ElapInventory {
  registeredColonies: number; programYear: number; stateCode?: string
  countyName?: string; fsaRegistrationId?: string; registrationDate?: string
  normalMortalityThresholdPct?: number
}
interface ElapEligibility {
  eligible: boolean; score: number; programYear: number; summary: string
  mortalityRate: number | null; estimatedPayout: number
  checks: ElapCheck[]; lossEvents: LossEvent[]; inventory: ElapInventory | null
}

// ─── Styled ───────────────────────────────────────────────────────────────────
const Page = styled.div`padding: 24px; max-width: 1100px; margin: 0 auto;`
const Title = styled.h1`font-size: 24px; font-weight: 700; margin-bottom: 4px; display: flex; align-items: center; gap: 10px;`
const Sub = styled.p`color: #6b7280; margin-bottom: 24px; font-size: 14px;`

const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin-bottom: 24px;`
const StatCard = styled.div<{color?: string}>`
  background: #fff; border-radius: 12px; padding: 16px; border: 1px solid #e5e7eb;
  border-left: 4px solid ${p => p.color || '#6366f1'};
`
const StatLabel = styled.div`font-size: 12px; color: #6b7280; margin-bottom: 4px; font-weight: 600; text-transform: uppercase;`
const StatValue = styled.div<{size?: string}>`font-size: ${p => p.size || '28px'}; font-weight: 800; color: #111827;`

const Card = styled.div`background: #fff; border-radius: 12px; padding: 20px; border: 1px solid #e5e7eb; margin-bottom: 16px;`
const CardTitle = styled.h2`font-size: 16px; font-weight: 700; margin-bottom: 16px; color: #111827;`

const CheckRow = styled.div<{status: string}>`
  display: flex; align-items: flex-start; gap: 12px; padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
`
const CheckDot = styled.div<{status: string}>`
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; margin-top: 4px;
  background: ${p => p.status === 'pass' ? '#10b981' : p.status === 'fail' ? '#ef4444' : p.status === 'warn' ? '#f59e0b' : '#9ca3af'};
`
const CheckLabel = styled.div`font-size: 14px; font-weight: 600; color: #111827;`
const CheckDetail = styled.div`font-size: 13px; color: #6b7280; margin-top: 2px;`
const Badge = styled.span<{color: string}>`
  padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700;
  background: ${p => p.color}22; color: ${p => p.color}; border: 1px solid ${p => p.color}44;
`

const ScoreBar = styled.div`background: #f3f4f6; border-radius: 8px; height: 12px; overflow: hidden; margin-top: 8px;`
const ScoreFill = styled.div<{pct: number, color: string}>`
  height: 100%; width: ${p => p.pct}%; background: ${p => p.color}; border-radius: 8px; transition: width 1s;
`

const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`text-align: start; padding: 10px 12px; background: #F3EBDB; color: #2B1A0F; font-weight: 700; font-size: 13px; border-bottom: 2px solid #D9C9A8; white-space: nowrap;`
const Td = styled.td`padding: 10px 12px; border-bottom: 1px solid #EDE4D3; color: #374151; vertical-align: top;`

const YearSelect = styled.select`padding: 8px 12px; border-radius: 8px; border: 1px solid #e5e7eb; font-size: 14px; background: #fff; cursor: pointer;`
const Btn = styled.button<{variant?: string}>`
  padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; font-size: 13px; font-weight: 600;
  background: ${p => p.variant === 'danger' ? '#ef4444' : p.variant === 'success' ? '#10b981' : '#6366f1'};
  color: #fff; transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`

const EligibleBanner = styled.div<{eligible: boolean}>`
  padding: 16px 20px; border-radius: 12px; margin-bottom: 24px;
  background: ${p => p.eligible ? '#d1fae5' : '#fee2e2'};
  border: 1px solid ${p => p.eligible ? '#6ee7b7' : '#fca5a5'};
  color: ${p => p.eligible ? '#065f46' : '#991b1b'};
  font-weight: 600; font-size: 15px; display: flex; align-items: center; gap: 10px;
`

const Modal = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex;
  align-items: center; justify-content: center; z-index: 100; padding: 16px;
`
const ModalBox = styled.div`
  background: #fff; border-radius: 12px; padding: 24px; width: 100%;
  max-width: 480px; max-height: 80vh; overflow-y: auto;
`
const Input = styled.input`
  width: 100%; padding: 8px 12px; border-radius: 8px; border: 1px solid #e5e7eb;
  font-size: 14px; margin-bottom: 12px;
`
const Label = styled.label`font-size: 13px; font-weight: 600; color: #374151; display: block; margin-bottom: 4px;`

// ─── Filing Guide Steps ───────────────────────────────────────────────────────
// Text lives in elap.guide.steps.<key>.{title,when,how,tip}; translated at render time.
const FILING_STEPS = [
  { step: 1, key: 'preRegister', critical: true, icon: '📋' },
  { step: 2, key: 'documentLoss', critical: true, icon: '📸' },
  { step: 3, key: 'noticeOfLoss', critical: true, icon: '🏛️' },
  { step: 4, key: 'application', critical: false, icon: '📄' },
  { step: 5, key: 'payment', critical: false, icon: '💰' },
]

const LOSS_CAUSES = ['disease', 'weather', 'predator', 'pesticide', 'varroa', 'starvation', 'other']

// Date-only strings ("YYYY-MM-DD") are parsed as local dates so they don't shift a day across time zones.
function fmtDate(d: string | undefined, locale: string) {
  if (!d) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d)
  const date = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d)
  if (isNaN(date.getTime())) return d
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ElapScreen() {
  const { t, i18n } = useTranslation()
  const fmt = (d?: string) => fmtDate(d, i18n.language)
  const [year, setYear] = useState(new Date().getFullYear())
  const [data, setData] = useState<ElapEligibility | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'dashboard'|'checklist'|'guide'>('dashboard')
  const [showInventoryModal, setShowInventoryModal] = useState(false)
  const [showLossModal, setShowLossModal] = useState(false)
  const [inv, setInv] = useState<Partial<ElapInventory>>({})
  const [loss, setLoss] = useState<any>({})
  const [yards, setYards] = useState<any[]>([])
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  const load = async () => {
    setLoading(true)
    try {
      const r = await axios.get(`${API_BASE_URL}/elap/eligibility?year=${year}`, { headers })
      setData(r.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [year])
  useEffect(() => {
    axios.get(`${API_BASE_URL}/yards`, { headers }).then(r => setYards(r.data || [])).catch(() => {})
  }, [])

  const saveInventory = async () => {
    await axios.post(`${API_BASE_URL}/elap/inventory`, { ...inv, programYear: year }, { headers })
    setShowInventoryModal(false); load()
  }

  const saveLoss = async () => {
    await axios.post(`${API_BASE_URL}/elap/loss-events`, { ...loss }, { headers })
    setShowLossModal(false); load()
  }

  const deleteLoss = async (id: string) => {
    if (!window.confirm(t('elap.confirmDeleteLoss'))) return
    await axios.delete(`${API_BASE_URL}/elap/loss-events/${id}`, { headers })
    load()
  }

  const scoreColor = (s: number) => s >= 75 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444'
  const statusColor = (s: string) => s === 'pass' ? '#10b981' : s === 'fail' ? '#ef4444' : s === 'warn' ? '#f59e0b' : '#9ca3af'
  const years = Array.from({length: 4}, (_, i) => new Date().getFullYear() - i)

  return (
    <Page>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8}}>
        <Title>🌾 {t('elap.title')}</Title>
        <div style={{display:'flex', gap:8, alignItems:'center'}}>
          <YearSelect value={year} onChange={e => setYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </YearSelect>
          <Btn onClick={() => setShowInventoryModal(true)}>📋 {t('elap.setInventory')}</Btn>
          <Btn variant="success" onClick={() => setShowLossModal(true)}>+ {t('elap.addLossEvent')}</Btn>
        </div>
      </div>
      <Sub>{t('elap.subtitle')}</Sub>

      {/* Tabs */}
      <div style={{display:'flex', borderBottom:'2px solid #e5e7eb', marginBottom: 24, gap: 0}}>
        {[
          {id:'dashboard', label:`📊 ${t('elap.tabs.dashboard')}`},
          {id:'checklist', label:`✅ ${t('elap.tabs.checklist')}`},
          {id:'guide', label:`📖 ${t('elap.tabs.guide')}`},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} style={{
            padding: '10px 20px', border:'none', background:'none', cursor:'pointer',
            fontWeight: 700, fontSize: 14,
            color: activeTab === tab.id ? '#6366f1' : '#6b7280',
            borderBottom: activeTab === tab.id ? '2px solid #6366f1' : '2px solid transparent',
            marginBottom: -2,
          }}>{tab.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{textAlign:'center', padding: 60, color: '#6b7280'}}>{t('elap.loadingData')}</div>
      ) : !data ? (
        <EligibleBanner eligible={false}>⚠️ {t('elap.loadError')}</EligibleBanner>
      ) : (
        <>
          {activeTab === 'dashboard' && <>
          <EligibleBanner eligible={data.eligible}>
            {data.eligible ? '✅' : '❌'}
            {' '}{data.eligible ? t('elap.likelyEligibleBanner') : t('elap.notYetEligibleBanner')}
            <span style={{marginRight:'auto'}}></span>
            <span style={{fontSize:13, fontWeight:400}}>{data.summary}</span>
          </EligibleBanner>

          {/* Stats */}
          <Grid>
            <StatCard color={scoreColor(data.score)}>
              <StatLabel>{t('elap.approvalScore')}</StatLabel>
              <StatValue>{data.score}<span style={{fontSize:16}}>/100</span></StatValue>
              <ScoreBar><ScoreFill pct={data.score} color={scoreColor(data.score)}/></ScoreBar>
            </StatCard>
            <StatCard color="#6366f1">
              <StatLabel>{t('elap.estimatedPayout')}</StatLabel>
              <StatValue size="24px">${data.estimatedPayout.toLocaleString()}</StatValue>
            </StatCard>
            <StatCard color="#f59e0b">
              <StatLabel>{t('elap.mortalityRate')}</StatLabel>
              <StatValue>{data.mortalityRate !== null ? `${data.mortalityRate.toFixed(1)}%` : '—'}</StatValue>
            </StatCard>
            <StatCard color="#10b981">
              <StatLabel>{t('elap.lossEvents')}</StatLabel>
              <StatValue>{data.lossEvents.length}</StatValue>
            </StatCard>
            <StatCard color="#8b5cf6">
              <StatLabel>{t('elap.registeredColonies')}</StatLabel>
              <StatValue>{data.inventory?.registeredColonies ?? '—'}</StatValue>
            </StatCard>
          </Grid>

          {/* Eligibility Checks */}
          <Card>
            <CardTitle>{t('elap.eligibilityChecklist')}</CardTitle>
            {data.checks.map(c => (
              <CheckRow key={c.id} status={c.status}>
                <CheckDot status={c.status}/>
                <div style={{flex:1}}>
                  <div style={{display:'flex', alignItems:'center', gap:8}}>
                    <CheckLabel>{c.label}</CheckLabel>
                    <Badge color={statusColor(c.status)}>{t(`elap.checkStatus.${c.status}`)}</Badge>
                    {c.required && <Badge color="#ef4444">{t('elap.required')}</Badge>}
                  </div>
                  <CheckDetail>{c.detail}</CheckDetail>
                </div>
              </CheckRow>
            ))}
          </Card>

          {/* Loss Events */}
          <Card>
            <CardTitle>{t('elap.lossEventsCount', { count: data.lossEvents.length })}</CardTitle>
            {data.lossEvents.length === 0 ? (
              <div style={{color:'#9ca3af', textAlign:'center', padding: 24}}>{t('elap.noLossEvents')}</div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('elap.table.date')}</Th><Th>{t('elap.table.yard')}</Th><Th>{t('elap.table.coloniesLost')}</Th>
                      <Th>{t('elap.table.cause')}</Th><Th>{t('elap.table.fsaNotice')}</Th><Th>{t('elap.table.deadline')}</Th><Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lossEvents.map(e => (
                      <tr key={e._id}>
                        <Td>{fmt(e.discoveredDate)}</Td>
                        <Td>{yards.find(y => y._id === e.yardId)?.name || e.yardId}</Td>
                        <Td><strong>{e.coloniesLost}</strong></Td>
                        <Td><Badge color="#6b7280">{LOSS_CAUSES.includes(e.cause) ? t(`elap.causes.${e.cause}`) : e.cause}</Badge></Td>
                        <Td>{e.fsaNoticeFiled ? <Badge color="#10b981">✓ {t('elap.noticeFiled')}</Badge> : <Badge color="#ef4444">✗ {t('elap.noticeMissing')}</Badge>}</Td>
                        <Td style={{color: new Date(e.filingDeadline) < new Date() ? '#ef4444' : '#10b981'}}>{fmt(e.filingDeadline)}</Td>
                        <Td><Btn variant="danger" onClick={() => deleteLoss(e._id)}>{t('elap.deleteEvent')}</Btn></Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>

          {/* FSA Report */}
          <Card>
            <CardTitle>📄 {t('elap.fsaReport.title')}</CardTitle>
            <div style={{fontFamily:'monospace', fontSize:13, background:'#f9fafb', padding:16, borderRadius:8, lineHeight:1.8}}>
              <div><strong>{t('elap.fsaReport.programYear')}:</strong> {data.programYear}</div>
              <div><strong>{t('elap.form.fsaRegistrationId')}:</strong> {data.inventory?.fsaRegistrationId || '—'}</div>
              <div><strong>{t('elap.fsaReport.stateCounty')}:</strong> {data.inventory?.stateCode || '—'} / {data.inventory?.countyName || '—'}</div>
              <div><strong>{t('elap.registeredColonies')}:</strong> {data.inventory?.registeredColonies ?? '—'}</div>
              <div><strong>{t('elap.fsaReport.totalColoniesLost')}:</strong> {data.lossEvents.reduce((s,e) => s + e.coloniesLost, 0)}</div>
              <div><strong>{t('elap.mortalityRate')}:</strong> {data.mortalityRate !== null ? `${data.mortalityRate.toFixed(1)}%` : '—'}</div>
              <div><strong>{t('elap.estimatedPayout')}:</strong> ${data.estimatedPayout.toLocaleString()}</div>
              <div><strong>{t('elap.fsaReport.eligibilityStatus')}:</strong> {data.eligible ? `✅ ${t('elap.fsaReport.likelyEligible')}` : `❌ ${t('elap.fsaReport.notEligible')}`}</div>
            </div>
          </Card>
          </>}

          {/* ─── CHECKLIST TAB ─────────────────────────────────── */}
          {activeTab === 'checklist' && <>
          <Card>
            <CardTitle>{t('elap.checklist.title')}</CardTitle>
            <p style={{fontSize:13, color:'#6b7280', marginBottom:16}}>{t('elap.checklist.subtitle')}</p>
            {data.checks.map((c, i) => (
              <div key={c.id} style={{display:'flex', gap:16, padding:'14px 0', borderBottom: i < data.checks.length-1 ? '1px solid #f3f4f6' : 'none'}}>
                <div style={{width:36,height:36,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18, background: c.status==='pass'?'#d1fae5':c.status==='fail'?'#fee2e2':c.status==='warn'?'#fef3c7':'#f3f4f6'}}>
                  {c.status==='pass'?'✅':c.status==='fail'?'❌':c.status==='warn'?'⚠️':'⬜'}
                </div>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontWeight:700,fontSize:14}}>{c.label}</span>
                    {c.required && <Badge color="#ef4444">{t('elap.required')}</Badge>}
                    <Badge color={c.status==='pass'?'#10b981':c.status==='fail'?'#ef4444':c.status==='warn'?'#f59e0b':'#9ca3af'}>{t(`elap.checkStatus.${c.status}`)}</Badge>
                  </div>
                  <div style={{fontSize:13,color:'#6b7280',marginTop:4}}>{c.detail}</div>
                  {c.status!=='pass'&&c.id==='pre_registration'&&<div style={{marginTop:8,padding:'8px 12px',background:'#eff6ff',borderRadius:6,fontSize:12,color:'#1e40af'}}>💡 <strong>{t('elap.checklist.action')}:</strong> {t('elap.checklist.actionPreRegistration')}</div>}
                  {c.status!=='pass'&&c.id==='filing_deadline'&&<div style={{marginTop:8,padding:'8px 12px',background:'#fff7ed',borderRadius:6,fontSize:12,color:'#9a3412'}}>💡 <strong>{t('elap.checklist.action')}:</strong> {t('elap.checklist.actionFilingDeadline')} <a href="https://www.farmers.gov/service-locator" target="_blank" rel="noreferrer" style={{color:'#1e40af'}}>farmers.gov/service-locator</a></div>}
                  {c.status!=='pass'&&c.id==='loss_events'&&<div style={{marginTop:8,padding:'8px 12px',background:'#eff6ff',borderRadius:6,fontSize:12,color:'#1e40af'}}>💡 <strong>{t('elap.checklist.action')}:</strong> {t('elap.checklist.actionLossEvents')}</div>}
                </div>
              </div>
            ))}
          </Card>
          {data.lossEvents.filter(e=>!e.fsaNoticeFiled).length>0&&(
            <div style={{background:'#fee2e2',border:'1px solid #fca5a5',borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontWeight:700,color:'#991b1b',fontSize:15,marginBottom:8}}>🚨 {t('elap.checklist.urgentMissingNotice', { total: data.lossEvents.filter(e=>!e.fsaNoticeFiled).length })}</div>
              {data.lossEvents.filter(e=>!e.fsaNoticeFiled).map(e=>{
                const daysLeft=Math.ceil((new Date(e.filingDeadline).getTime()-Date.now())/86400000)
                return(<div key={e._id} style={{fontSize:13,color:daysLeft<0?'#7f1d1d':'#9a3412',marginBottom:4}}>{daysLeft<0?`❌ ${t('elap.checklist.overdue')}`:`⏰ ${t('elap.checklist.daysLeft', { days: daysLeft })}`} — {t('elap.checklist.lossLine', { colonies: e.coloniesLost, date: fmt(e.discoveredDate), deadline: fmt(e.filingDeadline) })}</div>)
              })}
            </div>
          )}
          </>}

          {/* ─── FILING GUIDE TAB ──────────────────────────────── */}
          {activeTab === 'guide' && <>
          <div style={{marginBottom:16,padding:'12px 16px',background:'#eff6ff',borderRadius:10,fontSize:13,color:'#1e40af',border:'1px solid #bfdbfe'}}>
            📌 {t('elap.description')}
          </div>
          {FILING_STEPS.map(s=>(
            <Card key={s.step}>
              <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
                <div style={{fontSize:28,flexShrink:0}}>{s.icon}</div>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                    <span style={{fontWeight:800,fontSize:15}}>{t('elap.guide.stepTitle', { step: s.step, title: t(`elap.guide.steps.${s.key}.title`) })}</span>
                    {s.critical&&<Badge color="#ef4444">{t('elap.critical')}</Badge>}
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:'#6b7280',textTransform:'uppercase' as any,marginBottom:6}}>🕐 {t(`elap.guide.steps.${s.key}.when`)}</div>
                  <div style={{fontSize:13,color:'#374151',lineHeight:1.6,marginBottom:8}}><strong>{t('elap.guide.how')}:</strong> {t(`elap.guide.steps.${s.key}.how`)}</div>
                  <div style={{padding:'8px 12px',background:'#f0fdf4',borderRadius:6,fontSize:12,color:'#166534'}}>💡 {t(`elap.guide.steps.${s.key}.tip`)}</div>
                </div>
              </div>
            </Card>
          ))}
          <Card>
            <CardTitle>📞 {t('elap.resources.title')}</CardTitle>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {[
                {label:t('elap.resources.findFsa'),url:'https://www.farmers.gov/service-locator',desc:t('elap.resources.findFsaDesc')},
                {label:t('elap.resources.overview'),url:'https://www.fsa.usda.gov/programs-and-services/disaster-assistance-program/emergency-livestock-assistance/index',desc:t('elap.resources.overviewDesc')},
                {label:t('elap.resources.ccc852'),url:'https://www.fsa.usda.gov/Assets/USDA-FSA-Public/usdafiles/FSAO/pdf/CCC-852.pdf',desc:t('elap.resources.ccc852Desc')},
                {label:t('elap.resources.ccc853'),url:'https://www.fsa.usda.gov/Assets/USDA-FSA-Public/usdafiles/FSAO/pdf/CCC-853.pdf',desc:t('elap.resources.ccc853Desc')},
              ].map(r=>(
                <a key={r.url} href={r.url} target="_blank" rel="noreferrer" style={{display:'flex',gap:12,padding:'10px 14px',background:'#f9fafb',borderRadius:8,border:'1px solid #e5e7eb',textDecoration:'none',alignItems:'center'}}>
                  <span style={{fontSize:20}}>🔗</span>
                  <div><div style={{fontWeight:600,color:'#1e40af',fontSize:14}}>{r.label}</div><div style={{fontSize:12,color:'#6b7280'}}>{r.desc}</div></div>
                </a>
              ))}
            </div>
          </Card>
          <Card>
            <CardTitle>📊 {t('elap.yourNumbers', { year })}</CardTitle>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div><div style={{color:'#6b7280',fontSize:12}}>{t('elap.stats.registered')}</div><div style={{fontWeight:700,fontSize:18}}>{data.inventory?.registeredColonies??'—'}</div></div>
              <div><div style={{color:'#6b7280',fontSize:12}}>{t('elap.stats.totalLost')}</div><div style={{fontWeight:700,fontSize:18}}>{data.lossEvents.reduce((s,e)=>s+e.coloniesLost,0)}</div></div>
              <div><div style={{color:'#6b7280',fontSize:12}}>{t('elap.stats.mortality')}</div><div style={{fontWeight:700,fontSize:18}}>{data.mortalityRate?.toFixed(1)??'—'}%</div></div>
              <div><div style={{color:'#6b7280',fontSize:12}}>{t('elap.stats.estPayout')}</div><div style={{fontWeight:700,fontSize:18,color:'#10b981'}}>${data.estimatedPayout.toLocaleString()}</div></div>
            </div>
          </Card>
          </>}
        </>
      )}

      {/* Inventory Modal */}
      {showInventoryModal && (
        <Modal onClick={() => setShowInventoryModal(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <h2 style={{marginBottom:16, fontSize:18, fontWeight:700}}>📋 {t('elap.inventoryModal.title')}</h2>
            <Label>{t('elap.form.registeredColonies')}</Label>
            <Input type="number" placeholder={t('elap.inventoryModal.coloniesPlaceholder')} value={inv.registeredColonies || ''} onChange={e => setInv({...inv, registeredColonies: Number(e.target.value)})}/>
            <Label>{t('elap.form.registrationDate')}</Label>
            <Input type="date" value={inv.registrationDate || ''} onChange={e => setInv({...inv, registrationDate: e.target.value})}/>
            <Label>{t('elap.form.fsaRegistrationId')}</Label>
            <Input placeholder={t('elap.inventoryModal.optional')} value={inv.fsaRegistrationId || ''} onChange={e => setInv({...inv, fsaRegistrationId: e.target.value})}/>
            <Label>{t('elap.form.stateCode')}</Label>
            <Input placeholder={t('elap.inventoryModal.statePlaceholder')} value={inv.stateCode || ''} onChange={e => setInv({...inv, stateCode: e.target.value})}/>
            <Label>{t('elap.inventoryModal.countyName')}</Label>
            <Input placeholder={t('elap.inventoryModal.countyPlaceholder')} value={inv.countyName || ''} onChange={e => setInv({...inv, countyName: e.target.value})}/>
            <Label>{t('elap.inventoryModal.normalMortalityThreshold')}</Label>
            <Input type="number" placeholder="15" value={inv.normalMortalityThresholdPct || ''} onChange={e => setInv({...inv, normalMortalityThresholdPct: Number(e.target.value)})}/>
            <div style={{display:'flex', gap:8, marginTop:8}}>
              <Btn onClick={saveInventory} style={{flex:1}}>{t('button.save.text')}</Btn>
              <Btn variant="danger" onClick={() => setShowInventoryModal(false)} style={{flex:1}}>{t('button.cancel.text')}</Btn>
            </div>
          </ModalBox>
        </Modal>
      )}

      {/* Loss Event Modal */}
      {showLossModal && (
        <Modal onClick={() => setShowLossModal(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <h2 style={{marginBottom:16, fontSize:18, fontWeight:700}}>🐝 {t('elap.addLossEvent')}</h2>
            <Label>{t('elap.table.yard')}</Label>
            <select style={{width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:14, marginBottom:12}} value={loss.yardId||''} onChange={e => setLoss({...loss, yardId: e.target.value})}>
              <option value="">{t('elap.lossModal.selectYard')}</option>
              {yards.map(y => <option key={y._id} value={y._id}>{y.name}</option>)}
            </select>
            <Label>{t('elap.lossModal.discoveredDate')}</Label>
            <Input type="date" value={loss.discoveredDate||''} onChange={e => setLoss({...loss, discoveredDate: e.target.value})}/>
            <Label>{t('elap.table.coloniesLost')}</Label>
            <Input type="number" placeholder="0" value={loss.coloniesLost||''} onChange={e => setLoss({...loss, coloniesLost: Number(e.target.value)})}/>
            <Label>{t('elap.lossModal.hivesDestroyed')}</Label>
            <Input type="number" placeholder="0" value={loss.hivesDestroyed||''} onChange={e => setLoss({...loss, hivesDestroyed: Number(e.target.value)})}/>
            <Label>{t('elap.table.cause')}</Label>
            <select style={{width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:14, marginBottom:12}} value={loss.cause||''} onChange={e => setLoss({...loss, cause: e.target.value})}>
              <option value="">{t('elap.lossModal.selectCause')}</option>
              {LOSS_CAUSES.map(c => <option key={c} value={c}>{t(`elap.causes.${c}`)}</option>)}
            </select>
            <Label>{t('elap.lossModal.fsaNoticeFiled')}</Label>
            <select style={{width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:14, marginBottom:12}} value={loss.fsaNoticeFiled?.toString()||'false'} onChange={e => setLoss({...loss, fsaNoticeFiled: e.target.value === 'true'})}>
              <option value="false">{t('common.no')}</option>
              <option value="true">{t('common.yes')}</option>
            </select>
            <Label>{t('form.label.notes')}</Label>
            <Input placeholder={t('elap.lossModal.notesPlaceholder')} value={loss.notes||''} onChange={e => setLoss({...loss, notes: e.target.value})}/>
            <div style={{display:'flex', gap:8, marginTop:8}}>
              <Btn onClick={saveLoss} style={{flex:1}}>{t('button.save.text')}</Btn>
              <Btn variant="danger" onClick={() => setShowLossModal(false)} style={{flex:1}}>{t('button.cancel.text')}</Btn>
            </div>
          </ModalBox>
        </Modal>
      )}
    </Page>
  )
}
